import {getPerceivedBrightness} from '@Utils/color.util';
import {blobToDataUrl} from '@Utils/file.util';
import {isWebpSupported} from '@Utils/browser.util';
import {getImageItemBigScreenSizeRemovedBackgroundS3Key} from '@Libraries/image-item.library';
import {MAX_USER_IMAGE_DIMENSION} from '@Libraries/user-image-library';
import {pollableJob, resumePollableJob} from '@Libraries/pollable-job.library';
import {PollFailError, PollState} from '@Libraries/pollable-job.library.types';
import type {PollEnvelope, PollKickoffAck, PollTransportParams} from '@Libraries/pollable-job.library.types';

interface RemoveBackgroundResponse {
  status: string;
  remainingRemoveBackgroundCredits: number;
}

interface UploadingImageStatusResponse {
  status: UploadStatus;
  hashedFilename: string;
  fileExtension: string;
  errorCode?: UserImageUploadErrorCode;
}

interface UploadingImageStatusPollResponse {
  isAvailable: boolean;
  data: UploadingImageStatusResponse;
}

/**
 * SSE channel key prefix for the client-minted tempUploadingImageUID. Because the client mints
 * the UID, it can construct the channel key locally and subscribe before the upload body has
 * even reached the server. Must equal UserImageFacade::UPLOADING_IMAGE_CHANNEL_PREFIX on the
 * backend; changing either side strands in-flight deferred-upload subscribers.
 */
const UPLOADING_IMAGE_POLL_KEY_PREFIX = 'upload_uid_';

export enum UploadStatus {
  SUCCESS = 'image_upload_succeeded',
  FAILED = 'image upload failed',
}

export class UserImageUploadError extends Error {
  public errorCode?: UserImageUploadErrorCode;

  constructor(message: string, code?: UserImageUploadErrorCode) {
    super(message);
    this.name = 'UserImageUploadError';
    this.errorCode = code;
  }
}

export enum UserImageUploadErrorCode {
  IMAGE_DIMENSIONS_EXCEEDED = 'image_dimensions_exceeded',
  INVALID_IMAGE = 'invalid_image',
}

export interface ImageFileDimensions {
  width: number;
  height: number;
}

export const REMOVED_BG_IMAGE_COST = 1;
export const UPLOADING_IMAGE_STATUS_TIMEOUT = 600000;

const WHITE_DETECTION_SAMPLE_SIZE = 16;
const WHITE_LUMINANCE_THRESHOLD = 0.9;
const VISIBLE_ALPHA_THRESHOLD = 0.5;
const predominantlyWhiteCache = new Map<string, boolean>();

export const getClampedImageFileDimensions = async (file: File): Promise<ImageFileDimensions> => {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImageAsync(objectUrl);
    const scale = Math.min(1, MAX_USER_IMAGE_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight));
    return {
      width: Math.max(1, Math.round(image.naturalWidth * scale)),
      height: Math.max(1, Math.round(image.naturalHeight * scale)),
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const REMOVE_BACKGROUND_INSUFFICIENT_CREDITS_FAIL_CODE = 'INSUFFICIENT_CREDITS';
const REMOVE_BACKGROUND_LEGACY_INSUFFICIENT_CREDITS_CODE = 'insufficient_credits';
const DEFAULT_REMOVE_BACKGROUND_TIMEOUT_MS = 120_000;

export class RemoveBackgroundError extends Error {
  public readonly code: string;

  constructor(code: string) {
    super(code);
    this.name = 'RemoveBackgroundError';
    this.code = code;
  }
}

interface RemoveBackgroundDoneData {
  remainingRemoveBackgroundCredits: number;
}

const initiateImageBgRemovalV3 = async (imageHashedFilename: string, isUpscaled: boolean): Promise<PollKickoffAck> => {
  return (await window.PMW.writeLocal('posterimage/removeUserImageBackgroundV3', {
    userImageHashedFilename: imageHashedFilename,
    isUpscaled: isUpscaled ? '1' : '0',
  })) as PollKickoffAck;
};

const checkImageBgRemovalStatusV3 = async (ack: PollKickoffAck, transportParams: PollTransportParams): Promise<PollEnvelope<RemoveBackgroundDoneData>> => {
  return (await window.PMW.readLocal('posterimage/getImageBackgroundRemovalStatusV3', {
    pollingKey: ack.pollingKey,
    ...transportParams,
  })) as PollEnvelope<RemoveBackgroundDoneData>;
};

export const removeImageBackground = async (
  imageHashedFilename: string,
  isUpscaled = false,
  estimatedAjaxTimeoutInMilliseconds = DEFAULT_REMOVE_BACKGROUND_TIMEOUT_MS
): Promise<RemoveBackgroundResponse> => {
  try {
    const data = await pollableJob<RemoveBackgroundDoneData>({
      kickoff: () => initiateImageBgRemovalV3(imageHashedFilename, isUpscaled),
      status: checkImageBgRemovalStatusV3,
      timeoutMs: estimatedAjaxTimeoutInMilliseconds,
    });
    return {status: 'done', remainingRemoveBackgroundCredits: data.remainingRemoveBackgroundCredits};
  } catch (e) {
    if (e instanceof PollFailError && e.code === REMOVE_BACKGROUND_INSUFFICIENT_CREDITS_FAIL_CODE) {
      throw new RemoveBackgroundError(REMOVE_BACKGROUND_LEGACY_INSUFFICIENT_CREDITS_CODE);
    }
    throw e;
  }
};

export const getRemoveBackgroundCost = async (hashedFilename: string, imageSource: string): Promise<number> => {
  if (await doesRemovedBackgroundExists(hashedFilename, imageSource)) {
    return 0;
  }
  return REMOVED_BG_IMAGE_COST;
};

export const doesRemovedBackgroundExists = async (hashedFilename: string, imageSource: string, isUpscaled = false): Promise<boolean> => {
  return (await window.PMW.readLocal('posterimage/doesRemovedBackgroundExistsForImage', {
    userImageS3Key: getImageItemBigScreenSizeRemovedBackgroundS3Key(hashedFilename, imageSource, isUpscaled),
  })) as boolean;
};

/**
 * Returns whether the filetype is of an image
 * @param {string} fileType
 * @returns {boolean}
 */
export const isFiletypeImage = (fileType: string) => {
  if (fileType === '') {
    return false;
  }
  return fileType.includes('image/');
};

/**
 * Returns the browser compatible file extension
 * @param {string} fileExtension
 * @return {string}
 */
export const getCompatibleImageFileExtension = (fileExtension: string): string => {
  const FALLBACK_IMAGE_EXTENSION_FOR_WEBP = 'png';
  if (fileExtension === 'webp' && !isWebpSupported()) {
    return FALLBACK_IMAGE_EXTENSION_FOR_WEBP;
  }
  return fileExtension;
};

/**
 * @param {string} src
 * @returns {mise<number[]>Pro}
 */
export const getImageDimensions = async (src: string): Promise<[number, number]> => {
  const img = await loadImageAsync(src);
  return [img.naturalWidth, img.naturalHeight];
};

export const removeAlmostTranparentPixels = async (img: HTMLImageElement): Promise<HTMLImageElement> => {
  const ALPHA_THRESHOLD = 3;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error(`Failed to get context of canvas`);
  }

  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const imgd = ctx.getImageData(0, 0, img.width, img.height);
  const pix = imgd.data;

  for (let i = 0, n = pix.length; i < n; i += 4) {
    if (pix[i + 3] < ALPHA_THRESHOLD) {
      pix[i + 3] = 0;
    }
  }

  ctx.putImageData(imgd, 0, 0);
  const url = canvas.toDataURL();
  return loadImageAsync(url);
};

export const hasTransparency = (img: HTMLImageElement): boolean => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error(`Failed to get context of canvas`);
  }
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);

  const {data} = ctx.getImageData(0, 0, canvas.width, canvas.height);
  let hasAlphaPixels = false;
  for (let i = 3, n = data.length; i < n; i += 4) {
    if (data[i] < 255) {
      hasAlphaPixels = true;
      break;
    }
  }
  return hasAlphaPixels;
};

export const isImagePredominantlyWhite = (img: HTMLImageElement): boolean => {
  const cacheKey = img.currentSrc || img.src;
  const cached = predominantlyWhiteCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }

  const result = measureIsPredominantlyWhite(img);
  if (cacheKey) {
    predominantlyWhiteCache.set(cacheKey, result);
  }
  return result;
};

const measureIsPredominantlyWhite = (img: HTMLImageElement): boolean => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return false;
    }
    canvas.width = WHITE_DETECTION_SAMPLE_SIZE;
    canvas.height = WHITE_DETECTION_SAMPLE_SIZE;
    ctx.drawImage(img, 0, 0, WHITE_DETECTION_SAMPLE_SIZE, WHITE_DETECTION_SAMPLE_SIZE);

    const {data} = ctx.getImageData(0, 0, WHITE_DETECTION_SAMPLE_SIZE, WHITE_DETECTION_SAMPLE_SIZE);
    let luminanceSum = 0;
    let visiblePixelCount = 0;
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3] / 255;
      if (alpha < VISIBLE_ALPHA_THRESHOLD) {
        continue;
      }
      const luminance = getPerceivedBrightness(data[i], data[i + 1], data[i + 2]);
      luminanceSum += luminance;
      visiblePixelCount += 1;
    }
    if (visiblePixelCount === 0) {
      return false;
    }
    return luminanceSum / visiblePixelCount >= WHITE_LUMINANCE_THRESHOLD;
  } catch {
    return false;
  }
};

/**
 * @param {string} src
 * @returns {Promise<number>}
 */
export const getImageRatio = async (src: string): Promise<number> => {
  const [width, height] = await getImageDimensions(src);
  return width / height;
};

const inFlightImageLoads = new Map<string, Promise<HTMLImageElement>>();
const IMAGE_LOAD_MAX_ATTEMPTS = 3;
const IMAGE_LOAD_RETRY_BASE_DELAY_MS = 500;
const DATA_URL_PREFIX = 'data:';

/**
 * Loads image with a promise
 * @param src
 * @param setCrossOrigin pass false for display-only preloads of a URL that is also fetched plainly (by an <img> or CSS) —
 * a CORS-mode request would fail against the plain browser-cached copy when the CDN response lacks Vary: Origin
 * @return {Promise<HTMLImageElement>}
 */
export const loadImageAsync = (src: string, setCrossOrigin = true): Promise<HTMLImageElement> => {
  const inFlightKey = `${setCrossOrigin}:${src}`;
  const existing = inFlightImageLoads.get(inFlightKey);
  if (existing) {
    return existing;
  }
  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = (): void => {
      resolve(img);
    };
    if (setCrossOrigin) {
      img.crossOrigin = 'anonymous';
    }
    img.onerror = reject;
    img.src = src;
  }).finally(() => {
    inFlightImageLoads.delete(inFlightKey);
  });
  inFlightImageLoads.set(inFlightKey, promise);
  return promise;
};

export class ImageLoadError extends Error {
  public readonly url: string;
  public readonly attempts: number;

  constructor(url: string, attempts: number) {
    super(`Image failed to load after ${attempts} attempts: ${url}`);
    this.name = 'ImageLoadError';
    this.url = url;
    this.attempts = attempts;
  }
}

/**
 * Loads an image, retrying with exponential backoff so a transient network or CDN failure does not
 * fail the caller. Rejects with an ImageLoadError naming the URL once every attempt has failed.
 */
export const loadImageWithRetriesAsync = (src: string): Promise<HTMLImageElement> => {
  return loadImageFromAttempt(src, 1);
};

const loadImageFromAttempt = async (src: string, attempt: number): Promise<HTMLImageElement> => {
  try {
    return await loadImageAsync(src);
  } catch {
    if (attempt >= IMAGE_LOAD_MAX_ATTEMPTS) {
      throw new ImageLoadError(getLoggableImageUrl(src), attempt);
    }
    await waitForMilliseconds(IMAGE_LOAD_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
    return loadImageFromAttempt(src, attempt + 1);
  }
};

const getLoggableImageUrl = (src: string): string => {
  return src.startsWith(DATA_URL_PREFIX) ? DATA_URL_PREFIX : src;
};

const waitForMilliseconds = (milliseconds: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
};

export const loadImageSync = (url: string, successCB: (img: any) => any, failCB: () => any, setCrossOrigin = true): void => {
  const img = new Image();
  img.onload = successCB.bind(this, img);
  if (setCrossOrigin) {
    img.crossOrigin = 'anonymous';
  }
  img.onerror = failCB;
  img.onabort = failCB;
  img.src = url;
};

export const dataURLToArray = (imageDataURL: string): Promise<Uint8ClampedArray> => {
  return new Promise((resolve, reject) => {
    const imageElem = document.createElement('img');
    imageElem.src = imageDataURL;

    imageElem.onload = (): void => {
      const w = imageElem.width;
      const h = imageElem.height;

      const canvas = document.createElement('canvas');
      canvas.width = imageElem.width;
      canvas.height = imageElem.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error(`Failed to get context of canvas`));
        return;
      }
      ctx.drawImage(imageElem, 0, 0);

      resolve(ctx.getImageData(0, 0, w, h).data);
    };
  });
};

export const imageUrlToDataUrl = async (imageUrl: string): Promise<string> => {
  const response = await fetch(imageUrl);
  const blob = await response.blob();
  return blobToDataUrl(blob);
};

export const imageFileToDataUrl = async (imageFile: File): Promise<string | undefined> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e): void => {
      if (e.target) {
        const dataURL = e.target.result;
        resolve((dataURL as string) ?? undefined);
      }
      resolve(undefined);
    };
    reader.onerror = (error): void => {
      reject(error);
    };
    reader.readAsDataURL(imageFile);
  });
};

export const getUserImageHashedFilenameForUploadingImage = async (
  tempUploadingImageUIDRetrievalFunction: () => string | Promise<string>
): Promise<UploadingImageStatusResponse> => {
  const tempUploadingImageUID = await tempUploadingImageUIDRetrievalFunction();
  return resumePollableJob<UploadingImageStatusResponse>({
    pollingKey: UPLOADING_IMAGE_POLL_KEY_PREFIX + tempUploadingImageUID,
    status: checkUploadingImageStatus,
    timeoutMs: UPLOADING_IMAGE_STATUS_TIMEOUT,
  });
};

const checkUploadingImageStatus = async (ack: PollKickoffAck, transportParams: PollTransportParams): Promise<PollEnvelope<UploadingImageStatusResponse>> => {
  const response = (await window.PMW.readLocal('posterimage/getUserImageHashedFilenameForUploadingImage', {
    tempUploadingImageUID: ack.pollingKey.substring(UPLOADING_IMAGE_POLL_KEY_PREFIX.length),
    ...transportParams,
  })) as UploadingImageStatusPollResponse;

  if (response.isAvailable) {
    return {state: PollState.DONE, data: response.data};
  }
  return {state: PollState.PROCESSING};
};
